import React, { useMemo } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { Box, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RemoveCircle as RemoveCircleIcon,
  Block as BlockIcon,
} from '@mui/icons-material';

type StatusKind = 'compliant' | 'shortage' | 'overage' | 'unauthorized' | 'unknown';

const getStatusKind = (authorizedQty?: number | null, issuedQty?: number | null): StatusKind => {
  const a = typeof authorizedQty === 'number' ? authorizedQty : undefined;
  const i = typeof issuedQty === 'number' ? issuedQty : undefined;
  if (typeof a !== 'number' || typeof i !== 'number') return 'unknown';
  if (a === 0 && i > 0) return 'unauthorized';
  if (i > a) return 'overage';
  if (i === a) return 'compliant';
  if (i < a) return 'shortage';
  return 'unknown';
};

type StatusIndicatorProps = {
  authorizedQty?: number | null;
  issuedQty?: number | null;
  explicitKind?: StatusKind;
  size?: 'small' | 'medium';
  ariaLabel?: string;
};

const formatQuantity = (value?: number | null): string | undefined => {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return value.toLocaleString();
};

const summarizeQuantities = (
  issuedQty?: number | null,
  authorizedQty?: number | null,
): string | undefined => {
  const issuedText = formatQuantity(issuedQty);
  const authorizedText = formatQuantity(authorizedQty);
  if (issuedText && authorizedText) {
    return `${issuedText} issued / ${authorizedText} authorized`;
  }
  if (issuedText) return `${issuedText} issued`;
  if (authorizedText) return `${authorizedText} authorized`;
  return undefined;
};

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  authorizedQty,
  issuedQty,
  explicitKind,
  size = 'small',
  ariaLabel,
}) => {
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const kind: StatusKind = explicitKind ?? getStatusKind(authorizedQty, issuedQty);
  const quantitySummary = useMemo(
    () => summarizeQuantities(issuedQty, authorizedQty),
    [authorizedQty, issuedQty],
  );

  const resolved = useMemo(() => {
    switch (kind) {
      case 'compliant':
        return {
          label: ariaLabel ?? 'Compliant',
          tooltip: `Compliant — Issued quantity matches what is authorized${
            quantitySummary ? ` (${quantitySummary})` : ''
          }.`,
          icon: (
            <CheckCircleIcon
              fontSize={size}
              sx={{ color: (t) => t.palette.success.main }}
              titleAccess="Compliant"
            />
          ),
        };
      case 'shortage':
        return {
          label: ariaLabel ?? 'Shortage',
          tooltip: `Shortage — Issued fewer items than authorized${
            quantitySummary ? ` (${quantitySummary})` : ''
          }.`,
          icon: (
            <RemoveCircleIcon
              fontSize={size}
              sx={{ color: (t) => t.palette.warning.main }}
              titleAccess="Shortage"
            />
          ),
        };
      case 'overage':
        return {
          label: ariaLabel ?? 'Overage',
          tooltip: `Overage — Issued more items than authorized${
            quantitySummary ? ` (${quantitySummary})` : ''
          }.`,
          icon: (
            <CancelIcon
              fontSize={size}
              sx={{ color: (t) => t.palette.error.main }}
              titleAccess="Overage"
            />
          ),
        };
      case 'unauthorized':
        return {
          label: ariaLabel ?? 'Unauthorized',
          tooltip: `Unauthorized — Item is not on any assigned ${ieKitLabel}${
            quantitySummary ? ` (${quantitySummary})` : ''
          }.`,
          icon: (
            <BlockIcon
              fontSize={size}
              sx={{ color: (t) => t.palette.warning.main }}
              titleAccess="Unauthorized"
            />
          ),
        };
      default:
        return {
          label: ariaLabel ?? 'Unknown',
          tooltip: 'Unknown status — Not enough data is available to explain this indicator.',
          icon: (
            <RemoveCircleIcon
              fontSize={size}
              sx={{ color: (t) => t.palette.grey[500] }}
              titleAccess="Unknown"
            />
          ),
        };
    }
  }, [ariaLabel, kind, quantitySummary, size]);

  return (
    <Tooltip title={resolved.tooltip ?? resolved.label} enterDelay={300}>
      <Box
        component="span"
        role="img"
        aria-label={resolved.label}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
          lineHeight: 1,
        }}
      >
        {resolved.icon}
      </Box>
    </Tooltip>
  );
};
